// ui.tsx — PT-scoped primitives for the operator console.
//
// The app's shared `primitives.tsx` is bound to `T` (the tenant product theme:
// Source Serif/Outfit, green accent). Using it here would make the control plane
// look like the product, which platformTheme.ts explains is a safety property,
// not a preference. So the console has its own small kit built on `PT`.
//
// Deliberately small. This is not a component library — it is the handful of
// shapes the twelve console screens actually repeat.

import React, { useEffect, useRef, useState } from "react";
import { PT, PT_NUM } from "../../platformTheme";

// ── Layout ───────────────────────────────────────────────────────────────────

export const card: React.CSSProperties = {
  background: PT.bg2,
  border: `1px solid ${PT.brd}`,
  borderRadius: PT.radiusLg,
  padding: 16,
};

export const PCard: React.FC<{
  children?: React.ReactNode;
  style?: React.CSSProperties;
  onClick?: () => void;
  title?: string;
}> = ({ children, style, onClick, title }) => (
  <div
    onClick={onClick}
    title={title}
    style={{ ...card, ...(onClick ? { cursor: "pointer" } : null), ...style }}
  >
    {children}
  </div>
);

export const PSection: React.FC<{ title: string; sub?: string; right?: React.ReactNode; children?: React.ReactNode }> = ({
  title, sub, right, children,
}) => (
  <section style={{ marginBottom: 22 }}>
    <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 10 }}>
      <h2 style={{ fontSize: 14, fontWeight: 600, margin: 0, color: PT.tx }}>{title}</h2>
      {sub && <span style={{ fontSize: 11.5, color: PT.txD }}>{sub}</span>}
      <div style={{ flex: 1 }} />
      {right}
    </div>
    {children}
  </section>
);

// ── Buttons ──────────────────────────────────────────────────────────────────

type Variant = "primary" | "default" | "danger" | "ghost";

export const PBtn: React.FC<{
  children?: React.ReactNode;
  onClick?: () => void;
  v?: Variant;
  disabled?: boolean;
  small?: boolean;
  title?: string;
  style?: React.CSSProperties;
}> = ({ children, onClick, v = "default", disabled, small, title, style }) => {
  const palette: Record<Variant, React.CSSProperties> = {
    primary: { background: PT.acc, color: PT.txW, border: `1px solid ${PT.acc}` },
    default: { background: PT.bg2, color: PT.tx, border: `1px solid ${PT.brd}` },
    danger: { background: PT.bg2, color: PT.err, border: `1px solid ${PT.err}` },
    ghost: { background: "transparent", color: PT.txM, border: "1px solid transparent" },
  };
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={title}
      style={{
        fontFamily: PT.fontUI,
        fontSize: small ? 11.5 : 12.5,
        fontWeight: 500,
        padding: small ? "4px 9px" : "7px 13px",
        borderRadius: PT.radius,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.5 : 1,
        whiteSpace: "nowrap",
        ...palette[v],
        ...style,
      }}
    >
      {children}
    </button>
  );
};

// ── Status ───────────────────────────────────────────────────────────────────

export const PBadge: React.FC<{ children?: React.ReactNode; tone?: "ok" | "warn" | "err" | "info" | "neutral" }> = ({
  children, tone = "neutral",
}) => {
  const map = {
    ok: [PT.ok, PT.okS], warn: [PT.warn, PT.warnS], err: [PT.err, PT.errS],
    info: [PT.info, PT.infoS], neutral: [PT.neutral, PT.neutralS],
  } as const;
  const [fg, bg] = map[tone];
  return (
    <span style={{
      fontFamily: PT.fontMono, fontSize: 10, fontWeight: 600, letterSpacing: 0.4,
      textTransform: "uppercase", padding: "2px 6px", borderRadius: PT.radius,
      color: fg, background: bg, whiteSpace: "nowrap",
    }}>
      {children}
    </span>
  );
};

/** Tenant status → tone. Offboarded is neutral, not an error: it is a valid end state. */
export function statusTone(s?: string): "ok" | "warn" | "err" | "neutral" {
  switch ((s || "").toLowerCase()) {
    case "active": return "ok";
    case "suspended": return "warn";
    case "offboarded": return "neutral";
    default: return "neutral";
  }
}

export const PBanner: React.FC<{ tone?: "err" | "ok" | "warn" | "info"; children?: React.ReactNode }> = ({
  tone = "err", children,
}) => {
  const map = { ok: [PT.ok, PT.okS], warn: [PT.warn, PT.warnS], err: [PT.err, PT.errS], info: [PT.info, PT.infoS] } as const;
  const [fg, bg] = map[tone];
  if (!children) return null;
  return (
    <div style={{
      fontSize: 12.5, color: fg, background: bg, border: `1px solid ${fg}33`,
      borderRadius: PT.radius, padding: "8px 11px", marginBottom: 12,
    }}>
      {children}
    </div>
  );
};

export const PEmpty: React.FC<{ children?: React.ReactNode }> = ({ children }) => (
  <div style={{ padding: "26px 14px", textAlign: "center", color: PT.txD, fontSize: 12.5 }}>{children}</div>
);

export const PSpinner: React.FC<{ label?: string }> = ({ label = "Loading…" }) => (
  <div style={{ padding: "26px 14px", textAlign: "center", color: PT.txD, fontSize: 12.5 }}>{label}</div>
);

// ── Table ────────────────────────────────────────────────────────────────────

export const th: React.CSSProperties = {
  textAlign: "left", fontSize: 10, fontWeight: 600, letterSpacing: 0.5,
  textTransform: "uppercase", color: PT.txD, padding: "8px 10px",
  borderBottom: `1px solid ${PT.brd}`, whiteSpace: "nowrap",
};
export const td: React.CSSProperties = {
  fontSize: 12.5, color: PT.tx, padding: "9px 10px",
  borderBottom: `1px solid ${PT.brdSubtle}`, verticalAlign: "middle",
};
export const tdNum: React.CSSProperties = { ...td, ...PT_NUM, textAlign: "right" };

export const PTable: React.FC<{ head: React.ReactNode; children?: React.ReactNode; minWidth?: number }> = ({
  head, children, minWidth = 720,
}) => (
  // overflowX on the WRAPPER, not the page: a wide operator table must scroll
  // inside its card rather than making the whole console scroll sideways.
  <div style={{ overflowX: "auto" }}>
    <table style={{ width: "100%", minWidth, borderCollapse: "collapse" }}>
      <thead><tr>{head}</tr></thead>
      <tbody>{children}</tbody>
    </table>
  </div>
);

export const PRow: React.FC<{ children?: React.ReactNode; onClick?: () => void }> = ({ children, onClick }) => {
  const [hov, setHov] = useState(false);
  return (
    <tr
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{ cursor: onClick ? "pointer" : "default", background: hov && onClick ? PT.bgHov : "transparent" }}
    >
      {children}
    </tr>
  );
};

// ── Inputs ───────────────────────────────────────────────────────────────────

export const PInput: React.FC<{
  label?: string; value: string; onChange: (v: string) => void;
  placeholder?: string; type?: string; hint?: string; disabled?: boolean;
  mono?: boolean; autoFocus?: boolean;
}> = ({ label, value, onChange, placeholder, type = "text", hint, disabled, mono, autoFocus }) => (
  <label style={{ display: "block", marginBottom: 12 }}>
    {label && (
      <div style={{ fontSize: 10.5, fontWeight: 600, letterSpacing: 0.5, textTransform: "uppercase", color: PT.txD, marginBottom: 5 }}>
        {label}
      </div>
    )}
    <input
      type={type}
      value={value}
      autoFocus={autoFocus}
      disabled={disabled}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      style={{
        width: "100%", boxSizing: "border-box",
        fontFamily: mono ? PT.fontMono : PT.fontUI, fontSize: 12.5,
        padding: "7px 9px", borderRadius: PT.radius,
        border: `1px solid ${PT.brd}`, background: disabled ? PT.bg3 : PT.bgInset, color: PT.tx,
      }}
    />
    {hint && <div style={{ fontSize: 11, color: PT.txD, marginTop: 4 }}>{hint}</div>}
  </label>
);

export const PSelect: React.FC<{
  label?: string; value: string; onChange: (v: string) => void;
  options: { value: string; label: string }[]; hint?: string;
}> = ({ label, value, onChange, options, hint }) => (
  <label style={{ display: "block", marginBottom: 12 }}>
    {label && (
      <div style={{ fontSize: 10.5, fontWeight: 600, letterSpacing: 0.5, textTransform: "uppercase", color: PT.txD, marginBottom: 5 }}>
        {label}
      </div>
    )}
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      style={{
        width: "100%", boxSizing: "border-box", fontFamily: PT.fontUI, fontSize: 12.5,
        padding: "7px 9px", borderRadius: PT.radius,
        border: `1px solid ${PT.brd}`, background: PT.bgInset, color: PT.tx,
      }}
    >
      {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
    {hint && <div style={{ fontSize: 11, color: PT.txD, marginTop: 4 }}>{hint}</div>}
  </label>
);

export const PCheck: React.FC<{ checked: boolean; onChange: (v: boolean) => void; label: React.ReactNode }> = ({
  checked, onChange, label,
}) => (
  <label style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 12.5, color: PT.tx, cursor: "pointer", marginBottom: 8 }}>
    <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
    {label}
  </label>
);

/**
 * SECRET FIELD — the write-only pattern, in one place.
 *
 * A configured secret renders as the word CONFIGURED and an "enter to replace"
 * affordance. The current value is NEVER fetched, never rendered, never present
 * in the DOM. The backend does not return it (see `has_secret` on the operator
 * SSO read), and this component is built so that a future component author does
 * not have to remember that — there is no prop to pass a value in.
 */
export const PSecretField: React.FC<{
  label: string;
  configured: boolean;
  value: string;                 // the REPLACEMENT being typed, never the stored one
  onChange: (v: string) => void;
  hint?: string;
  required?: boolean;
}> = ({ label, configured, value, onChange, hint, required }) => {
  const [editing, setEditing] = useState(!configured);
  useEffect(() => { if (!configured) setEditing(true); }, [configured]);
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 10.5, fontWeight: 600, letterSpacing: 0.5, textTransform: "uppercase", color: PT.txD, marginBottom: 5 }}>
        {label}
      </div>
      {configured && !editing ? (
        <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
          <PBadge tone="ok">Configured</PBadge>
          <PBtn small onClick={() => { setEditing(true); onChange(""); }}>Enter to replace</PBtn>
        </div>
      ) : (
        <>
          <input
            type="password"
            value={value}
            autoComplete="new-password"
            placeholder={configured ? "Enter a new value to replace the stored secret" : "Enter the secret"}
            onChange={(e) => onChange(e.target.value)}
            style={{
              width: "100%", boxSizing: "border-box", fontFamily: PT.fontMono, fontSize: 12.5,
              padding: "7px 9px", borderRadius: PT.radius,
              border: `1px solid ${PT.brd}`, background: PT.bgInset, color: PT.tx,
            }}
          />
          {configured && (
            <div style={{ marginTop: 5 }}>
              <PBtn small v="ghost" onClick={() => { setEditing(false); onChange(""); }}>Keep the stored secret</PBtn>
            </div>
          )}
        </>
      )}
      <div style={{ fontSize: 11, color: PT.txD, marginTop: 5 }}>
        {hint || "Write-only. Validated on save and never returned by the API."}
        {required && !configured && " Required."}
      </div>
    </div>
  );
};

// ── Modal ────────────────────────────────────────────────────────────────────

export const PModal: React.FC<{
  open: boolean; onClose: () => void; title: React.ReactNode;
  children?: React.ReactNode; footer?: React.ReactNode; w?: number;
}> = ({ open, onClose, title, children, footer, w = 560 }) => {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, background: "rgba(16,24,40,.42)",
        display: "flex", alignItems: "flex-start", justifyContent: "center",
        padding: "8vh 16px", zIndex: 1000,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: PT.bg2, border: `1px solid ${PT.brd}`, borderRadius: PT.radiusLg,
          width: "100%", maxWidth: w, maxHeight: "84vh", overflowY: "auto",
          fontFamily: PT.fontUI, boxShadow: "0 24px 60px rgba(16,24,40,.22)",
        }}
      >
        <div style={{ padding: "13px 16px", borderBottom: `1px solid ${PT.brdSubtle}`, fontSize: 13.5, fontWeight: 600 }}>
          {title}
        </div>
        <div style={{ padding: 16 }}>{children}</div>
        {footer && (
          <div style={{ padding: "12px 16px", borderTop: `1px solid ${PT.brdSubtle}`, display: "flex", gap: 8, justifyContent: "flex-end" }}>
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * REASON PROMPT — the human half of the audit contract.
 *
 * The backend accepts a default reason if none is sent, which means a screen that
 * forgets to ask still "works" and writes a meaningless audit row. This prompt is
 * therefore the enforcement point on the UI side: it will not submit until the
 * operator has typed something substantive.
 *
 * `confirmName` implements the delete-permanently rule: the operator must type
 * the organisation name exactly, matching the server's `confirm_name` check, so
 * the destructive action cannot be completed by reflex.
 */
export const PReasonPrompt: React.FC<{
  open: boolean;
  title: string;
  detail?: React.ReactNode;
  danger?: boolean;
  confirmName?: string;
  busy?: boolean;
  error?: string;
  onCancel: () => void;
  onConfirm: (reason: string) => void;
}> = ({ open, title, detail, danger, confirmName, busy, error, onCancel, onConfirm }) => {
  const [reason, setReason] = useState("");
  const [typed, setTyped] = useState("");
  useEffect(() => { if (open) { setReason(""); setTyped(""); } }, [open]);

  // 2026-07-29 owner decision: the reason is OPTIONAL. The audit entry is not —
  // the server still records who/what/when/which-tenant for every action, and
  // substitutes "operator console action" when no reason is typed.
  //
  // The typed-name confirmation for destructive actions is UNTOUCHED and still
  // required. That gate exists to stop you deleting the wrong organisation by
  // accident, which is a different job from explaining why — dropping it here
  // would have quietly removed a safety control along with a paperwork one.
  const nameOk = !confirmName || typed.trim() === confirmName.trim();
  const ready = nameOk && !busy;

  return (
    <PModal
      open={open}
      onClose={onCancel}
      title={title}
      footer={
        <>
          <PBtn onClick={onCancel} disabled={busy}>Cancel</PBtn>
          <PBtn v={danger ? "danger" : "primary"} disabled={!ready} onClick={() => onConfirm(reason.trim())}>
            {busy ? "Working…" : danger ? "Confirm" : "Continue"}
          </PBtn>
        </>
      }
    >
      <PBanner tone="err">{error}</PBanner>
      {detail && <div style={{ fontSize: 12.5, color: PT.txM, marginBottom: 13 }}>{detail}</div>}
      <PInput
        label="Reason (optional — recorded in the operator audit log)"
        value={reason}
        onChange={setReason}
        autoFocus
        placeholder="Why is this action being taken? (optional)"
        hint="Optional. The action is recorded either way — who, what and when are always logged."
      />
      {confirmName && (
        <PInput
          label={`Type the organisation name to confirm`}
          value={typed}
          onChange={setTyped}
          mono
          placeholder={confirmName}
          hint={nameOk ? "Matches." : "Must match exactly."}
        />
      )}
    </PModal>
  );
};

// ── Misc ─────────────────────────────────────────────────────────────────────

export const PStat: React.FC<{ label: string; value: React.ReactNode; sub?: string }> = ({ label, value, sub }) => (
  <div style={{ ...card, padding: 13 }}>
    <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: 0.55, textTransform: "uppercase", color: PT.txD }}>{label}</div>
    <div style={{ ...PT_NUM, fontSize: 22, fontWeight: 600, marginTop: 6, color: PT.tx }}>{value}</div>
    {sub && <div style={{ fontSize: 11, color: PT.txD, marginTop: 3 }}>{sub}</div>}
  </div>
);

export const PGrid: React.FC<{ children?: React.ReactNode; min?: number }> = ({ children, min = 190 }) => (
  <div style={{ display: "grid", gridTemplateColumns: `repeat(auto-fill, minmax(${min}px, 1fr))`, gap: 12 }}>
    {children}
  </div>
);

export const PTabs: React.FC<{
  tabs: { id: string; label: string }[]; active: string; onChange: (id: string) => void;
}> = ({ tabs, active, onChange }) => (
  <div style={{ display: "flex", gap: 2, borderBottom: `1px solid ${PT.brd}`, marginBottom: 16, overflowX: "auto" }}>
    {tabs.map((t) => {
      const on = t.id === active;
      return (
        <button
          key={t.id}
          onClick={() => onChange(t.id)}
          style={{
            fontFamily: PT.fontUI, fontSize: 12.5, fontWeight: on ? 600 : 500,
            padding: "9px 13px", border: "none", background: "transparent",
            color: on ? PT.acc : PT.txM, borderBottom: `2px solid ${on ? PT.acc : "transparent"}`,
            cursor: "pointer", whiteSpace: "nowrap",
          }}
        >
          {t.label}
        </button>
      );
    })}
  </div>
);

/** Short, stable rendering of a uuid. Full value in the title attribute. */
export const PId: React.FC<{ id?: string | null }> = ({ id }) => {
  if (!id) return <span style={{ color: PT.txD }}>—</span>;
  return (
    <span title={id} style={{ ...PT_NUM, fontSize: 11, color: PT.txD }}>
      {String(id).slice(0, 8)}
    </span>
  );
};

export function fmtDate(v?: string | null): string {
  if (!v) return "—";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(undefined, { year: "numeric", month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" });
}

/** Focus trap helper for the palette: returns a ref to autofocus on mount. */
export function useAutoFocus<T extends HTMLElement>() {
  const ref = useRef<T | null>(null);
  useEffect(() => { ref.current?.focus(); }, []);
  return ref;
}
